import React, { lazy, Suspense, useSyncExternalStore } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { FeaturesProvider } from './contexts/FeaturesContext';
import { FeatureGate } from './components/FeatureGate';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { TeamPermissionsProvider } from './contexts/TeamPermissionsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { DeveloperCredit } from './components/DeveloperCredit';
import { MarketTicker } from './components/MarketTicker';
import ScrollToTop from './components/ScrollToTop';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { GlobalGestures } from './components/GlobalGestures';
import { ReAuthDialog } from './components/ReAuthDialog';
import { EmailImportReviewDialog } from './components/EmailImportReviewDialog';
import { useAuth } from './contexts/AuthContext';
import { subscribePrivateMode, isPrivateMode } from './utils/formatCurrency';
import './App.css';

// Perf #1 — Route-based code splitting. Login + Landing (entry points) load
// eagerly so the first paint is instant; every other page is downloaded
// only when the user navigates there. Drops main bundle from ~459 KB to
// ~120 KB gzipped on the metric that matters most: time-to-first-paint
// for an unauthenticated visitor.
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { StorageOverageDue } from './pages/StorageOverageDue';

const Register        = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const VerifyEmail     = lazy(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const ForgotPassword  = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword   = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Dashboard       = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Portfolios      = lazy(() => import('./pages/Portfolios').then(m => ({ default: m.Portfolios })));
const Reports         = lazy(() => import('./pages/Reports').then(m => ({ default: m.Reports })));
const NetWorth        = lazy(() => import('./pages/NetWorth'));
const Settings        = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Admin           = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const AdminParserSamples = lazy(() => import('./pages/AdminParserSamples').then(m => ({ default: m.AdminParserSamples })));
const MyUploads       = lazy(() => import('./pages/MyUploads').then(m => ({ default: m.MyUploads })));
const Support         = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));
const Manual          = lazy(() => import('./pages/Manual').then(m => ({ default: m.Manual })));
const AdminSupport    = lazy(() => import('./pages/AdminSupport').then(m => ({ default: m.AdminSupport })));
const Wallet          = lazy(() => import('./pages/Wallet').then(m => ({ default: m.Wallet })));
const AdminWallet     = lazy(() => import('./pages/AdminWallet').then(m => ({ default: m.AdminWallet })));
const TaxHarvesting   = lazy(() => import('./pages/TaxHarvesting').then(m => ({ default: m.TaxHarvesting })));
const Insurance       = lazy(() => import('./pages/InsuranceHub').then(m => ({ default: m.InsuranceHub })));
const Members         = lazy(() => import('./pages/Members').then(m => ({ default: m.Members })));
const Lifeline        = lazy(() => import('./pages/Lifeline').then(m => ({ default: m.Lifeline })));
const TeamAccess      = lazy(() => import('./pages/TeamAccess').then(m => ({ default: m.TeamAccess })));
const AcceptInvite    = lazy(() => import('./pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })));
const Pricing         = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess').then(m => ({ default: m.SubscriptionSuccess })));
const Contact         = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Privacy         = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Terms           = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Refund          = lazy(() => import('./pages/Refund').then(m => ({ default: m.Refund })));
const About           = lazy(() => import('./pages/About').then(m => ({ default: m.About })));

// Skeleton shown for ~50-300ms while a lazy route fetches its chunk. Kept
// minimal so it doesn't itself add weight. Same ink-green palette as the
// app shell so the transition feels intentional.
const RouteFallback = () => (
  <div
    data-testid="route-loading"
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: '#FAFAF7' }}
  >
    <div className="flex flex-col items-center gap-3">
      <div
        className="h-8 w-8 rounded-full border-2 animate-spin"
        style={{ borderColor: '#E5E5DF', borderTopColor: '#1B4332' }}
      />
      <span className="text-xs tracking-widest" style={{ color: '#78716C' }}>
        LOADING
      </span>
    </div>
  </div>
);

// Iter-194 — Auth-gated wrapper for the IMAP-import review dialog.
// Mounted globally (in App below) so it surfaces on ANY post-login page,
// not just /dashboard. Skips the fetch when the user isn't authenticated.
const GlobalEmailImportReview = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <EmailImportReviewDialog />;
};

function App() {
  // Subscribe to the global privacy-mode flag so the entire React tree
  // re-renders whenever the user toggles masking on/off. Without this,
  // components that call formatINR / formatQuantity but don't themselves
  // useContext(PrivacyContext) would render stale values until something
  // else (data refetch, navigation) forced their re-render. See the
  // PrivacyContext file for the producer side.
  useSyncExternalStore(subscribePrivateMode, isPrivateMode, () => false);

  return (
    <AuthProvider>
      <FeaturesProvider>
      <PrivacyProvider>
        <TeamPermissionsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <MarketTicker />
          <KeyboardShortcuts />
          <GlobalGestures />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public landing + legal pages */}
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Landing />} />
            <Route path="/pricing"  element={<Pricing />} />
            <Route path="/contact"  element={<Contact />} />
            <Route path="/about"    element={<About />} />
            <Route path="/privacy"  element={<Privacy />} />
            <Route path="/terms"    element={<Terms />} />
            <Route path="/refund"   element={<Refund />} />
            <Route path="/login" element={<Login />} />
            <Route path="/storage-overage-due" element={<StorageOverageDue />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route
              path="/payments/success"
              element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolios"
              element={
                <ProtectedRoute>
                  <Portfolios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolios/consolidated/:cid"
              element={
                <ProtectedRoute>
                  <Portfolios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolios/:id"
              element={
                <ProtectedRoute>
                  <Portfolios />
                </ProtectedRoute>
              }
            />
            <Route
              path="/net-worth"
              element={
                <ProtectedRoute>
                  <NetWorth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tax-harvesting"
              element={
                <ProtectedRoute>
                  <TaxHarvesting />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insurance"
              element={
                <ProtectedRoute>
                  <FeatureGate feature="insurance">
                    <Insurance />
                  </FeatureGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <Members />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lifeline"
              element={
                <ProtectedRoute>
                  <FeatureGate feature="lifeline">
                    <Lifeline />
                  </FeatureGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-access"
              element={
                <ProtectedRoute>
                  <FeatureGate feature="team_access">
                    <TeamAccess />
                  </FeatureGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/parser-samples"
              element={
                <ProtectedRoute>
                  <AdminParserSamples />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-uploads"
              element={
                <ProtectedRoute>
                  <MyUploads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <Support />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manuals"
              element={
                <ProtectedRoute>
                  <Manual />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/support"
              element={
                <ProtectedRoute>
                  <AdminSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <Wallet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/wallet"
              element={
                <ProtectedRoute>
                  <AdminWallet />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
          <Toaster />
          <ReAuthDialog />
          {/* Iter-194 — Mount the IMAP-import review dialog GLOBALLY,
              not just on Dashboard. Many users (especially the family-
              CA persona) bookmark a portfolio page and never visit the
              Dashboard after login → the review dialog never had a
              chance to mount, so IMAP-parsed contract notes stayed
              "pending review" forever and never landed in
              Holdings/Transactions. The wrapper hook gates the fetch
              on auth state, so unauthenticated visitors don't see a
              spurious 401 in the console. */}
          <GlobalEmailImportReview />
          <DeveloperCredit />
        </BrowserRouter>
        </TeamPermissionsProvider>
      </PrivacyProvider>
      </FeaturesProvider>
    </AuthProvider>
  );
}

export default App;
